// Project Sentinel - Protected Route Component
// Wraps a route to require a minimum role (SEN-261). Shows access denied below it.

import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/auth-context'
import { isAdminRole, type RoleCode } from '@/lib/roles'
import { ShieldAlert } from 'lucide-react'

interface ProtectedRouteProps {
  minRole?: RoleCode
  children: React.ReactNode
}

export function ProtectedRoute({ minRole, children }: ProtectedRouteProps) {
  const { user, isLoading, hasRole } = useAuth()

  if (isLoading) {
    return null // AuthProvider is still loading
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Admins (customer or platform) see every page
  if (isAdminRole(user.role)) {
    return <>{children}</>
  }

  if (minRole && !hasRole(minRole)) {
    return <AccessDenied />
  }

  return <>{children}</>
}

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <ShieldAlert className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
      <p className="text-sm text-muted-foreground max-w-md">
        You don't have permission to access this page. Contact your administrator if you believe this is an error.
      </p>
    </div>
  )
}
